import React from "react";
import "./Styles.css";

const RelativePaths = () => (
  <div className="content">
    <h1>Relative Paths</h1>

    <h2>What are Relative Paths?</h2>
    <p>
      Relative paths are a way to specify the location of a directory relative
      to another directory. Instead of specifying the full path to a file or
      directory, you specify a path that starts from your current directory.
    </p>

    <h2>Relative vs. Absolute Paths</h2>
    <p>
      An absolute path is a complete path from the root of the file system to
      the file or directory it points to. It starts with a `/` on Unix-based
      systems, or a drive letter (like `C:\`) on Windows. For example,
      `/Users/myUser/myProject/myFile.js` is an absolute path.
    </p>
    <p>
      On the other hand, a relative path starts from the current directory. For
      example, if your current directory is `/Users/myUser/myProject/`, the
      relative path to the same file would be `./myFile.js` or simply
      `myFile.js`.
    </p>

    <h2>Navigating with Relative Paths</h2>
    <p>
      Using relative paths, you can easily navigate the file structure of your
      project. Here are some basic rules:
    </p>
    <ul>
      <li>
        <strong>Same directory:</strong> If the file you're trying to access is
        in the same directory, you simply use its name. For example, if you're
        in the same directory as `myFile.js`, the relative path is `./myFile.js`
        or just `myFile.js`.
      </li>
      <li>
        <strong>Subdirectory:</strong> If the file is in a subdirectory, you
        include the name of the subdirectory. For example, if `myFile.js` is in
        a subdirectory named `mySubdirectory`, the relative path is
        `./mySubdirectory/myFile.js`.
      </li>
      <li>
        <strong>Parent directory:</strong> If the file is in a parent directory,
        you use `../` to navigate up one level. For example, if `myFile.js` is
        in the parent directory, the relative path is `../myFile.js`.
      </li>
      <li>
        <strong>Subdirectory of a parent:</strong> You can combine these to
        navigate complex structures. For example, if `myFile.js` is in a
        subdirectory of a parent directory, the relative path is
        `../mySubdirectory/myFile.js`.
      </li>
    </ul>
    <p>
      Understanding how to use relative paths is crucial for correctly linking
      files together in a project, especially when preparing for deployment to a
      system like ArDrive.
    </p>

    <h2>Why are Relative Paths Important for ArDrive?</h2>
    <p>
      When you deploy a project on ArDrive, all files are stored in the Arweave
      permaweb, a decentralized network of servers. Since there's no central
      root directory, absolute paths don't work because they can't point to a
      specific location in a traditional file system.
    </p>
    <p>
      On the other hand, relative paths work perfectly because they only refer
      to other files within the same project. When you use relative paths in
      your project, the ArDrive web app can correctly link all your files
      together when you upload your project.
    </p>

    <h2>Using Relative Paths in your dApp</h2>
    <p>
      Configure your dApp to use relative filepaths regardless of what domain is
      used to access it by adding the following line to your package.json file:
      <pre>"homepage": "./",</pre>
    </p>
    <p>
      Beyond this, make sure that all internal links and imports use relative
      filepaths, and not absolute ones.
    </p>
    <p>
      Using relative paths is a crucial part of ensuring your React dApp works
      correctly when deployed to ArDrive.
    </p>
  </div>
);

export default RelativePaths;
